import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { createMenuItem } from "@/utils/menuUtils";
import { MenuInfo } from "@rc-component/menu/es/interface";
import { Avatar, Divider, Dropdown } from "antd";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { Permission } from "../../../common/security/enums";
import { hasPermission } from "../../../common/security/utils";
import { AgentProfilePictureEditor } from "../../../modules/agent/components/AgentProfilePictureEditor";
import { formatAgentAggregatedName } from "../../../modules/agent/utils";
import { useCurrentAccount, useCurrentUser, useHasMultipleAccounts } from "../../../modules/auth/hooks";
import { endSession, switchAccount } from "../../../modules/auth/session";
import { DASHBOARD_ROUTE_PATHS } from "../../../modules/dashboard/paths";
import { CURRENT_USER_ROUTE_PATHS } from "../../../modules/user/paths";
import { AgentUserAccount, ClientUserAccount } from "../../../modules/user/types";
import { isAgentUserAccount } from "../../../modules/user/utils";
import styles from "./UserMenuView.module.scss";

const MENU_KEY = {
  LOGOUT: "logout",
  PROFILE: "profile",
  COMMISSIONS: "commissions",
};

export const UserMenuView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const user = useCurrentUser();
  const userAccount = useCurrentAccount();
  const hasMultipleAccounts = useHasMultipleAccounts();
  const routerLocation = useLocation();

  const handleMenuClick = (event: MenuInfo) => {
    const { key } = event;

    switch (key) {
      case MENU_KEY.LOGOUT:
        endSession();
        break;
      case MENU_KEY.PROFILE:
      case MENU_KEY.COMMISSIONS:
        break;
      default:
        if (key !== userAccount?.id) {
          switchAccount(key);
          navigate(DASHBOARD_ROUTE_PATHS.homepage.to);
        }
        break;
    }
  };

  const getUserMenuItems = (userAccount: AgentUserAccount | ClientUserAccount) => {
    const userMenuItems: ItemType[] = [];

    const menuItem = isAgentUserAccount(userAccount)
      ? createMenuItem({
          path: CURRENT_USER_ROUTE_PATHS.profile.to,
          key: MENU_KEY.PROFILE,
          label: t("navigation.header.user.profile"),
          icon: <AntIcon type="user" />,
          permissions: userAccount?.permissions,
          routerLocation,
        })
      : undefined;

    if (menuItem) {
      userMenuItems.push(menuItem);
    }

    if (hasPermission(userAccount.permissions, Permission.PROFILE_COMMISSIONS)) {
      const menuItem = createMenuItem({
        path: CURRENT_USER_ROUTE_PATHS.commissions.to,
        key: MENU_KEY.COMMISSIONS,
        label: t("navigation.header.user.commissions"),
        icon: <AntIcon type="euro" />,
        permissions: userAccount.permissions,
        routerLocation,
      });

      if (menuItem) {
        userMenuItems.push(menuItem);
      }
    }

    if (userMenuItems.length) {
      userMenuItems.push({ type: "divider", key: "user-dropdown-divider-1" });
    }

    if (hasMultipleAccounts) {
      let path: string | undefined;

      const orderedAccounts = isAgentUserAccount(userAccount)
        ? user?.agentUserRole?.userAccounts
            ?.filter((account) => account.confirmed && !account.disabled && account.agent)
            .sort((a, b) => {
              const accountATreePath = a.agent?.accessTreePath ?? "";
              const accountBTreePath = b.agent?.accessTreePath ?? "";

              return accountATreePath.localeCompare(accountBTreePath);
            })
            .reduce<MenuItemType[]>((accumulator, account, index) => {
              if (index === 0) {
                path = account.agent?.accessTreePath;
              } else if (!account.agent?.accessTreePath?.startsWith(path ?? "")) {
                path = account.agent?.accessTreePath;

                accumulator.push({
                  key: `${account.id}-divider`,
                  label: <Divider style={{ margin: 0 }} />,
                });
              }

              accumulator.push({
                key: account.id,
                label: account.representingAgent ? (
                  <div style={{ display: "block" }}>
                    <div>{formatAgentAggregatedName(account.agent)}</div>
                    <div className={styles.representingAgentName}>
                      {formatAgentAggregatedName(account.representingAgent)}
                    </div>
                  </div>
                ) : (
                  formatAgentAggregatedName(account.agent)
                ),
              });

              return accumulator;
            }, [])
        : user?.clientUserRole?.userAccounts
            ?.filter((account) => account.confirmed && !account.disabled)
            .map<MenuItemType>((account) => ({ key: account.id, label: account.clients?.[0]?.aggregatedName }));

      userMenuItems.push(
        {
          type: "group",
          key: "user-dropdown-group-accounts",
          label: (
            <ActionTextIcon icon="user-switch" text={t("navigation.header.user.accounts")} className={styles.text} />
          ),
          children: orderedAccounts,
        },
        { type: "divider", key: "user-dropdown-divider-2" },
      );
    }

    userMenuItems.push({ key: MENU_KEY.LOGOUT, label: t("navigation.header.logout"), icon: <AntIcon type="logout" /> });

    return userMenuItems;
  };

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      styles={{ root: { height: window.innerHeight - 60 } }}
      menu={{
        style: { maxHeight: window.innerHeight - 60 },
        items: userAccount ? getUserMenuItems(userAccount) : [],
        selectedKeys: userAccount?.id ? [userAccount.id] : [],
        onClick: handleMenuClick,
      }}
    >
      <div className={styles.action}>
        {isAgentUserAccount(userAccount) && userAccount.agent.profilePicture ? (
          <AgentProfilePictureEditor
            className={styles.accountProfilePicture}
            agent={userAccount.agent}
            width={38}
            preview={false}
            showActions={false}
          />
        ) : (
          <Avatar className={styles.accountAvatar} size="small" icon={<AntIcon type="user" />} />
        )}

        <div>
          <span className={styles.accountName}>{user?.name}</span>
          <span className={styles.accountName}>
            {isAgentUserAccount(userAccount)
              ? formatAgentAggregatedName(userAccount?.agent)
              : userAccount?.clients?.[0]?.aggregatedName}
          </span>
        </div>
      </div>
    </Dropdown>
  );
};
